import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Onboarding } from "@/components/auth/mobile/Onboarding";
import { OnboardingPaywall } from "@/components/auth/onboarding/OnboardingPaywall";
import { useAppSession } from "@/providers/AppSessionProvider";
import { useTheme } from "@/providers/ThemeProvider";
import { LanguageSelector } from "@/components/LanguageSelector";
import { isNativeApp } from "@/lib/platform";
import { purchasePackage, getPackageIdForType, restorePurchases, type PurchaseType } from "@/lib/purchases";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

type OnboardingPhase = "onboarding" | "paywall";

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated } = useAppSession();
  const { theme, setTheme } = useTheme();
  const [showLanguageSelector, setShowLanguageSelector] = useState(true);
  const [phase, setPhase] = useState<OnboardingPhase>("onboarding");
  const [isProcessing, setIsProcessing] = useState(false);

  // Force dark mode for onboarding page
  useEffect(() => {
    const previousTheme = theme;
    if (theme !== 'dark') {
      setTheme('dark');
    }
    return () => {
      // Restore previous theme on unmount
      if (previousTheme !== 'dark') {
        setTheme(previousTheme);
      }
    };
  }, []);

  // Redirect authenticated users to home
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);

  const handleComplete = () => {
    // After completing onboarding steps, show the paywall before auth
    setPhase("paywall");
  };

  const handleSkipToAuth = () => {
    // Even when skipping, show the paywall first
    setPhase("paywall");
  };

  const handleStartTrial = async (selectedPlan: PurchaseType) => {
    if (isNativeApp()) {
      // NATIVE APP: Purchase via Apple/Google BEFORE auth
      // RevenueCat supports anonymous purchases - no login required
      // After signup, we'll associate the purchase with the account
      const packageId = getPackageIdForType(selectedPlan);
      if (!packageId) {
        toast.error(t('paywall.invalid_plan'));
        return;
      }

      setIsProcessing(true);
      try {
        const toastId = toast.loading(t('paywall.processing'));
        const result = await purchasePackage(packageId);
        toast.dismiss(toastId);

        if (result.success) {
          // Store flag so post-auth flow knows to associate this purchase
          localStorage.setItem('pending_revenuecat_association', 'true');
          toast.success(t('paywall.purchase_successful'));
          navigate("/auth?signup=true&from=onboarding");
        } else {
          // User cancelled or error
          const errorLower = (result.error || '').toLowerCase();
          if (errorLower.includes('cancel')) {
            // User cancelled - don't show error, just stay on paywall
            console.log('[Onboarding] Purchase cancelled by user');
          } else {
            toast.error(result.error || t('paywall.purchase_failed'));
          }
        }
      } catch (error: any) {
        console.error('[Onboarding] Purchase error:', error);
        toast.error(error.message || t('paywall.purchase_error'));
      } finally {
        setIsProcessing(false);
      }
    } else {
      // WEB BROWSER: Auth first, then payment via Stripe on /upgrade
      navigate("/auth?signup=true&from=onboarding");
    }
  };

  const handleRestore = async () => {
    setIsProcessing(true);
    try {
      const result = await restorePurchases();
      if (result.success) {
        localStorage.setItem('pending_revenuecat_association', 'true');
        toast.success(t('paywall.purchases_restored'));
        navigate("/auth?signup=true&from=onboarding");
      } else {
        toast.error(result.error || t('paywall.no_purchases_found'));
      }
    } catch (error: any) {
      toast.error(error.message || t('paywall.restore_failed'));
    } finally {
      setIsProcessing(false);
    }
  };

  if (phase === "paywall") {
    return (
      <>
        <div className="fixed top-4 right-4 z-50">
          <LanguageSelector />
        </div>
        <OnboardingPaywall
          onStartTrial={handleStartTrial}
          onRestore={handleRestore}
          isProcessing={isProcessing}
        />
      </>
    );
  }

  return (
    <>
      {showLanguageSelector && (
        <div className="fixed top-4 right-4 z-50">
          <LanguageSelector />
        </div>
      )}
      <Onboarding
        onComplete={handleComplete}
        onSkipToAuth={handleSkipToAuth}
        onStepChange={(step) => setShowLanguageSelector(step !== 'generate')}
      />
    </>
  );
};

export default OnboardingPage;
